'use client'

import * as React from 'react'
import { OTPInput, OTPInputContext } from 'input-otp'
import { MinusIcon } from 'lucide-react'

import cn from '@config/tailwind/cn'

import { typographyVariants } from '../lib/typography'

function InputOTP({
  className,
  containerClassName,
  ...props
}: React.ComponentProps<typeof OTPInput> & {
  containerClassName?: string
}) {
  return (
    <OTPInput
      data-slot='input-otp'
      containerClassName={cn('cn-input-otp ui:flex ui:items-center ui:has-disabled:opacity-50', containerClassName)}
      spellCheck={false}
      className={cn('ui:disabled:cursor-not-allowed', className)}
      {...props}
    />
  )
}

function InputOTPGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='input-otp-group'
      className={cn(
        'ui:has-aria-invalid:ring-error/20 ui:has-aria-invalid:border-error ui:flex ui:items-center ui:gap-2 ui:has-aria-invalid:ring-[3px]',
        className,
      )}
      {...props}
    />
  )
}

function InputOTPSlot({
  index,
  size = 'lg',
  className,
  ...props
}: React.ComponentProps<'div'> & {
  index: number
  size?: 'lg' | 'sm'
}) {
  const inputOTPContext = React.useContext(OTPInputContext)
  const { char, hasFakeCaret, isActive } = inputOTPContext.slots[index] ?? {}

  return (
    <div
      data-slot='input-otp-slot'
      data-active={isActive}
      className={cn(
        'ui:border-blue-100 ui:data-[active=true]:border-blue-100 ui:data-[active=true]:ring-blue-100/50 ui:data-[active=true]:aria-invalid:ring-error/20 ui:aria-invalid:border-error ui:data-[active=true]:aria-invalid:border-error ui:bg-neutral-white ui:relative ui:flex ui:items-center ui:justify-center ui:rounded-md ui:border ui:p-2 ui:transition-all ui:outline-none ui:data-[active=true]:z-10 ui:data-[active=true]:ring-[3px]',
        size === 'lg'
          ? ['ui:size-16', typographyVariants({ variant: 'headline-lg' })]
          : ['ui:size-12', typographyVariants({ variant: 'headline-sm' })],
        className,
      )}
      {...props}
    >
      {char}
      {hasFakeCaret && (
        <div className='ui:pointer-events-none ui:absolute ui:inset-0 ui:flex ui:items-center ui:justify-center'>
          <div className='ui:animate-caret-blink ui:bg-foreground ui:h-4 ui:w-px ui:duration-1000' />
        </div>
      )}
    </div>
  )
}

function InputOTPSeparator({ ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='input-otp-separator'
      className="ui:flex ui:items-center ui:[&_svg:not([class*='size-'])]:size-4 ui:text-blue-100"
      role='separator'
      {...props}
    >
      <MinusIcon />
    </div>
  )
}

export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator }
export { REGEXP_ONLY_DIGITS_AND_CHARS, REGEXP_ONLY_DIGITS, REGEXP_ONLY_CHARS } from 'input-otp'
